// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import AppDialog from "@/components/common/AppDialog";
import { FormInput, FormTextarea, FormToggleGroup } from "@/components/forms/FormControls";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { DEAL_UPDATE_SCOPES } from "@/constant/deals.constant";
import { AskScope } from "@/enum/common.enum";
import { dealUpdateSchema } from "@/schemas/deals";

const DEFAULTS = { subject: "", body: "", scope: AskScope.AGENT_ONLY };

/**
 * Agent-initiated message to their client — the other half of the Ask-anything
 * channel. It opens a thread in the same place a client's question lands, so
 * both sides read one conversation rather than two parallel ones.
 */
export default function UpdateDialog({ open, onClose, onSubmit, busy }) {
  const form = useForm({ resolver: zodResolver(dealUpdateSchema), defaultValues: DEFAULTS });

  // A cancelled draft shouldn't reappear the next time the dialog opens.
  useEffect(() => {
    if (open) form.reset(DEFAULTS);
  }, [form, open]);

  return (
    <AppDialog
      open={open}
      onOpenChange={(next) => { if (!next) onClose(); }}
      title="Send an update"
      eyebrow="New"
      description="Opens a thread your client can reply to — here and by email."
      contentClassName="sm:max-w-lg"
      testId="deal-update-dialog"
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5" data-testid="update-modal" noValidate>
          <FormToggleGroup
            name="scope"
            label="Who should see this?"
            options={DEAL_UPDATE_SCOPES.map((scope) => ({ ...scope, testId: `update-scope-${scope.value}` }))}
            className="grid grid-cols-1 gap-2"
            itemClassName="h-auto justify-start border border-dash-border bg-white px-3 py-2.5 text-left text-xs text-dash-muted hover:bg-dash-searchBg"
            renderOption={(scope, active) => {
              const Icon = scope.Icon;
              return (
                <>
                  <Icon className={active ? "text-dash-greenDeep" : "text-dash-muted"} />
                  <span>
                    <strong className="block text-dash-ink">{scope.label}</strong>
                    <span className="mt-0.5 block text-[11px] leading-tight text-dash-muted">{scope.hint}</span>
                  </span>
                </>
              );
            }}
          />
          <FormInput name="subject" label="Subject" maxLength={140} placeholder="e.g. Inspection is booked for Thursday" data-testid="update-subject" required />
          <FormTextarea name="body" label="Update" maxLength={4000} rows={5} placeholder="Plain words are fine — this is what your client reads." className="resize-none" data-testid="update-body" required />
          <div className="grid grid-cols-2 gap-3 pt-2 sm:flex sm:justify-end">
            <Button type="button" variant="dashboardOutline" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="dashboardPrimary" loading={busy} data-testid="update-submit">Send update</Button>
          </div>
        </form>
      </Form>
    </AppDialog>
  );
}
